import React__default, { useMemo, useCallback } from 'react';
import { useController } from 'react-hook-form';
import { Chip } from '../../../atoms/Chip.js';
import { Note } from '../../../atoms/Note.js';
import { Text } from '../../../atoms/Text.js';
import { InfoIcon } from '../../../icons/InfoIcon.js';
import { WarningIcon } from '../../../icons/WarningIcon.js';
import { SearchInput } from '../../SearchInput.js';
import { InfoIconWithTooltip } from '../../Tooltip/ContentWithTooltip.js';
import { ReadOnlyField } from './ReadOnlyFormField.js';
import './ChipPickerFormField.scss';

/**
 * ChipPickerFormField - A form field that combines a searchable dropdown
 * with chip-based display for multi-select functionality.
 *
 * The field value is stored as an array of option IDs (`string[]`).
 *
 * @example
 * ```tsx
 * const schema = z.object({ vehicles: z.array(z.string()).optional() });
 *
 * <Form schema={schema} onSubmit={handleSubmit}>
 *   <ChipPickerFormField
 *     schemaField="vehicles"
 *     labelText="Restricted vehicles"
 *     descriptionText="Limit which vehicles appear in the vehicle and model year selectors."
 *     options={[
 *       { id: 'ninja', label: 'Ninja' },
 *       { id: 'hayabusa', label: 'Hayabusa' },
 *     ]}
 *     emptyText="No vehicles restricted"
 *     helperText="Selected vehicles will be hidden from the vehicle and model year selectors."
 *   />
 * </Form>
 * ```
 */
function ChipPickerFormField({ schemaField, labelText, descriptionText, isReadOnly, isDisabled, required, className, id, options, placeholder = 'Search', emptyText = 'No items selected', helperText, chipAppearance = 'outline', chipSize = 'small', showResultsOnFocus, maxItems, }) {
    const { field, fieldState } = useController({
        name: schemaField,
        shouldUnregister: false,
    });
    const selectedIds = Array.isArray(field.value) ? field.value : [];
    // Filter out already-selected options for the search
    const availableOptions = useMemo(() => {
        return options.filter((option) => !selectedIds.includes(option.id));
    }, [options, selectedIds]);
    // Handle search - filter available options by query
    const handleSearch = useCallback((query) => {
        if (!query.trim()) {
            return Promise.resolve(availableOptions.map((option) => ({
                value: option.id,
                label: option.label,
            })));
        }
        const lowerQuery = query.toLowerCase();
        return Promise.resolve(availableOptions
            .filter((option) => option.label.toLowerCase().includes(lowerQuery))
            .map((option) => ({
            value: option.id,
            label: option.label,
        })));
    }, [availableOptions]);
    const isAtMax = maxItems !== undefined && selectedIds.length >= maxItems;
    const handleSelect = useCallback((value) => {
        if (value && !selectedIds.includes(value) && !isAtMax) {
            field.onChange([...selectedIds, value]);
        }
    }, [selectedIds, field, isAtMax]);
    const handleRemove = useCallback((idToRemove) => {
        field.onChange(selectedIds.filter((selectedId) => selectedId !== idToRemove));
    }, [selectedIds, field]);
    const errorMessage = fieldState.error?.message;
    // Resolve selected option labels
    const selectedOptions = selectedIds
        .map((selectedId) => options.find((o) => o.id === selectedId))
        .filter(Boolean);
    // Read-only mode
    if (isReadOnly) {
        const displayValue = selectedOptions.length > 0 ? selectedOptions.map((o) => o.label).join(', ') : emptyText;
        return (React__default.createElement(ReadOnlyField, { label: labelText, value: displayValue, description: descriptionText, required: required, className: className }));
    }
    const isRequired = !!required;
    const generatedId = React__default.useId();
    const fieldId = id || generatedId;
    return (React__default.createElement("div", { "data-rev-component": 'chip-picker-form-field', "data-rev-invalid": !!errorMessage, "data-rev-required-field": isRequired, className: className },
        labelText && (React__default.createElement("label", { className: 'form-field-label--rev', htmlFor: fieldId },
            React__default.createElement("span", { className: 'form-field-label-text--rev' },
                labelText,
                descriptionText && (React__default.createElement("span", { className: 'form-field-tooltip-icon--rev' },
                    React__default.createElement(InfoIconWithTooltip, { tooltipContent: descriptionText, tooltipProps: { placement: 'top' } })))))),
        React__default.createElement("div", { className: 'chip-picker-layout--rev' },
            React__default.createElement("div", { className: 'chip-picker-search-section--rev' },
                React__default.createElement("div", { className: 'chip-picker-search-wrapper--rev' },
                    React__default.createElement(SearchInput, { handleSearch: handleSearch, onSelectItem: handleSelect, placeholder: isAtMax ? `Maximum of ${maxItems} selected` : placeholder, isDisabled: isDisabled || isAtMax, variant: 'outline', size: 'large', hasError: !!errorMessage, showResultsOnFocus: showResultsOnFocus })),
                helperText && (React__default.createElement("div", { className: 'chip-picker-helper-row--rev' },
                    React__default.createElement("div", { className: 'chip-picker-icon--rev' },
                        React__default.createElement(InfoIcon, null)),
                    React__default.createElement("div", { className: 'chip-picker-helper--rev' },
                        React__default.createElement(Text, { size: 'caption', color: errorMessage ? 'error' : 'secondary' }, helperText))))),
            React__default.createElement("div", { className: 'chip-picker-chips-section--rev' }, selectedOptions.length === 0 ? (React__default.createElement(Note, null, emptyText)) : (React__default.createElement("div", { className: 'chip-picker-chips-container--rev' },
                React__default.createElement(Text, { size: 'bodySmall', weight: 'semibold' }, labelText
                    ? `${labelText} (${selectedOptions.length})`
                    : `Selected (${selectedOptions.length})`),
                React__default.createElement("div", { className: 'chip-picker-chips-wrap--rev' }, selectedOptions.map((option) => (React__default.createElement(Chip, { key: option.id, appearance: chipAppearance, size: chipSize, disabled: isDisabled, onClose: isDisabled ? undefined : () => handleRemove(option.id), closeButtonDisabled: isDisabled }, option.label)))))))),
        errorMessage && (React__default.createElement("div", { className: 'chip-picker-error-row--rev' },
            React__default.createElement("div", { className: 'chip-picker-icon--rev' },
                React__default.createElement(WarningIcon, null)),
            React__default.createElement(Text, { size: 'caption', color: 'error' }, errorMessage)))));
}

export { ChipPickerFormField };
//# sourceMappingURL=ChipPickerFormField.js.map
